/**
 * Le compositor qui fabrique le flux spectateur tourne dans un Chrome headless
 * dépourvu des décodeurs propriétaires : une piste HEVC y produit une image
 * noire alors qu'elle se lit sans problème sur la machine du présentateur.
 * On lit donc le codec dans le conteneur avant d'accepter le fichier.
 */

export type VideoCodecInspection =
  | { status: 'supported'; fourcc: string }
  | { status: 'unsupported'; fourcc: string }
  | { status: 'unknown' };

/** Variantes H.264 : paramètres hors bande (avc1) ou en bande (avc3). */
const BROADCASTABLE_FOURCCS = ['avc1', 'avc3'];

/** `moov` est parfois écrit en fin de fichier, d'où la double lecture. */
export const PROBE_BYTES = 2 * 1024 * 1024;

const BOX_HEADER_BYTES = 8;
const LARGE_SIZE_HEADER_BYTES = 16;

type Box = {
  type: string;
  contentStart: number;
  end: number;
};

function readUint32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] << 24) >>> 0) +
    (bytes[offset + 1] << 16) +
    (bytes[offset + 2] << 8) +
    bytes[offset + 3]
  );
}

function readType(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(
    bytes[offset],
    bytes[offset + 1],
    bytes[offset + 2],
    bytes[offset + 3],
  );
}

/**
 * Les bornes sont volontairement tronquées à `limit` : sur une lecture
 * partielle du fichier, un conteneur incomplet doit livrer ce qu'il contient
 * plutôt que d'échouer.
 */
function readBox(bytes: Uint8Array, offset: number, limit: number): Box | null {
  if (offset + BOX_HEADER_BYTES > limit) {
    return null;
  }

  const declaredSize = readUint32(bytes, offset);
  const type = readType(bytes, offset + 4);

  let headerBytes = BOX_HEADER_BYTES;
  let boxSize = declaredSize;

  if (declaredSize === 1) {
    if (offset + LARGE_SIZE_HEADER_BYTES > limit) {
      return null;
    }
    boxSize = readUint32(bytes, offset + 8) * 2 ** 32 + readUint32(bytes, offset + 12);
    headerBytes = LARGE_SIZE_HEADER_BYTES;
  } else if (declaredSize === 0) {
    boxSize = limit - offset;
  }

  if (boxSize < headerBytes) {
    return null;
  }

  return {
    type,
    contentStart: offset + headerBytes,
    end: Math.min(offset + boxSize, limit),
  };
}

function findChild(bytes: Uint8Array, start: number, end: number, type: string): Box | null {
  let offset = start;
  while (offset < end) {
    const box = readBox(bytes, offset, end);
    if (!box || box.end <= offset) {
      return null;
    }
    if (box.type === type) {
      return box;
    }
    offset = box.end;
  }
  return null;
}

function eachChild(
  bytes: Uint8Array,
  start: number,
  end: number,
  type: string,
  visit: (box: Box) => VideoCodecInspection | null,
): VideoCodecInspection | null {
  let offset = start;
  while (offset < end) {
    const box = readBox(bytes, offset, end);
    if (!box || box.end <= offset) {
      return null;
    }
    if (box.type === type) {
      const result = visit(box);
      if (result) {
        return result;
      }
    }
    offset = box.end;
  }
  return null;
}

function isVideoTrack(bytes: Uint8Array, mdia: Box): boolean {
  const hdlr = findChild(bytes, mdia.contentStart, mdia.end, 'hdlr');
  if (!hdlr || hdlr.contentStart + 12 > hdlr.end) {
    return false;
  }
  // version/flags (4) + pre_defined (4), puis le handler_type.
  return readType(bytes, hdlr.contentStart + 8) === 'vide';
}

function readSampleFourcc(bytes: Uint8Array, stsd: Box): string | null {
  // version/flags (4) + entry_count (4), puis la première entrée : size (4) + format (4).
  const firstEntry = stsd.contentStart + 8;
  if (firstEntry + 8 > stsd.end) {
    return null;
  }
  return readType(bytes, firstEntry + 4);
}

function classify(fourcc: string): VideoCodecInspection {
  return BROADCASTABLE_FOURCCS.includes(fourcc)
    ? { status: 'supported', fourcc }
    : { status: 'unsupported', fourcc };
}

function inspectTrak(bytes: Uint8Array, trak: Box): VideoCodecInspection | null {
  const mdia = findChild(bytes, trak.contentStart, trak.end, 'mdia');
  if (!mdia || !isVideoTrack(bytes, mdia)) {
    return null;
  }

  const minf = findChild(bytes, mdia.contentStart, mdia.end, 'minf');
  const stbl = minf && findChild(bytes, minf.contentStart, minf.end, 'stbl');
  const stsd = stbl && findChild(bytes, stbl.contentStart, stbl.end, 'stsd');
  if (!stsd) {
    return null;
  }

  const fourcc = readSampleFourcc(bytes, stsd);
  return fourcc ? classify(fourcc) : null;
}

/**
 * Repère les `moov` par signature plutôt qu'en déroulant les boîtes depuis le
 * début : sur une lecture partielle, la boîte `mdat` qui les précède dépasse
 * la fenêtre lue et rendrait le parcours séquentiel impossible.
 */
function locateMoovStarts(bytes: Uint8Array): number[] {
  const starts: number[] = [];
  for (let offset = 4; offset + 4 <= bytes.length; offset += 1) {
    if (
      bytes[offset] === 0x6d && // m
      bytes[offset + 1] === 0x6f && // o
      bytes[offset + 2] === 0x6f && // o
      bytes[offset + 3] === 0x76 // v
    ) {
      starts.push(offset - 4);
    }
  }
  return starts;
}

export function findVideoCodec(bytes: Uint8Array): VideoCodecInspection {
  for (const moovStart of locateMoovStarts(bytes)) {
    const moov = readBox(bytes, moovStart, bytes.length);
    if (!moov) {
      continue;
    }
    const result = eachChild(bytes, moov.contentStart, moov.end, 'trak', (trak) =>
      inspectTrak(bytes, trak),
    );
    if (result) {
      return result;
    }
  }
  return { status: 'unknown' };
}

export function isBroadcastableVideoCodec(inspection: VideoCodecInspection): boolean {
  return inspection.status === 'supported';
}

/** `FileReader` plutôt que `Blob.arrayBuffer`, absent des Safari les plus anciens. */
function readSlice(file: File, start: number, end: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsArrayBuffer(file.slice(start, end));
  });
}

export async function inspectVideoFile(file: File): Promise<VideoCodecInspection> {
  const fromHead = findVideoCodec(await readSlice(file, 0, PROBE_BYTES));
  if (fromHead.status !== 'unknown' || file.size <= PROBE_BYTES) {
    return fromHead;
  }

  return findVideoCodec(await readSlice(file, file.size - PROBE_BYTES, file.size));
}
